import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS } from '../../constants/api-urls';
import './RFQSentSuccess.scss';

export default function RFQSentSuccess() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [rfq, setRfq] = useState(null);
  const [inspectorsCount, setInspectorsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get notified count from navigation state (passed from RequestInspectionQuotation page)
    if (location.state?.notifiedCount !== undefined) {
      setInspectorsCount(location.state.notifiedCount);
    }
    
    if (location.state?.rfqId) {
      fetchRFQDetails();
    } else {
      // Try to get RFQ by auction
      fetchRFQByAuction();
    }
  }, [auctionId, location.state]);

  const fetchRFQDetails = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_RFQ(location.state.rfqId));
      if (response && response.success) {
        setRfq(response.data);
        // If notifiedCount wasn't passed in state, try to fetch it from backend
        if (location.state?.notifiedCount === undefined) {
          fetchInspectorsCountFromBackend(response.data.rfq_id);
        }
      }
    } catch (error) {
      console.error('Error fetching RFQ:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRFQByAuction = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_RFQ_BY_AUCTION(auctionId));
      if (response && response.success && response.data) {
        setRfq(response.data);
        // If notifiedCount wasn't passed in state, try to fetch it from backend
        if (location.state?.notifiedCount === undefined) {
          fetchInspectorsCountFromBackend(response.data.rfq_id);
        }
      }
    } catch (error) {
      console.error('Error fetching RFQ by auction:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectorsCountFromBackend = async (rfqId) => {
    try {
      // Fetch notified inspectors count from backend
      // This would require a new API endpoint or we can use the existing getRFQNotifiedInspectors
      // For now, if we don't have the count from state, we'll show 0
      // TODO: Add API endpoint to get notified inspectors count for an RFQ
      console.log('Fetching notified inspectors count for RFQ:', rfqId);
    } catch (error) {
      console.error('Error fetching inspectors count:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="rfq-sent-success loading">Loading...</div>;
  }

  return (
    <div className="rfq-sent-success">
      <div className="success-container">
        {/* Success Icon */}
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="#28a745"/>
              <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <h1 className="success-title">RFQ Sent Successfully!</h1>
        <p className="success-description">
          Your inspection request has been sent to qualified inspectors in the region.
        </p>

        {/* RFQ Details Box */}
        <div className="rfq-details-box">
          <div className="detail-row">
            <div className="detail-item">
              <span className="detail-label">RFQ ID</span>
              <span className="detail-value">{rfq?.rfq_number || 'RFQ-UCTION-1'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Expected Response</span>
              <span className="detail-value">Within 24 hours</span>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-item">
              <span className="detail-label">Inspectors Notified</span>
              <span className="detail-value">{inspectorsCount} Qualified Inspectors</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Inspection Date</span>
              <span className="detail-value">{rfq ? formatDate(rfq.preferred_date) : '14/1/2026'}</span>
            </div>
          </div>
        </div>

        {/* Information Alert */}
        <div className="info-alert">
          <div className="alert-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM11 14H9V12H11V14ZM11 10H9V6H11V10Z" fill="currentColor"/>
            </svg>
          </div>
          <div className="alert-content">
            <p>
              You will receive quotations from inspectors via email. Review and select the inspector that best fits your requirements. 
              The inspection must be completed within 72 hours of scheduling.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn-primary" onClick={() => navigate(`/auction/${auctionId}/review-quotations`)}>
            View QA Quotations
          </button>
          <button className="btn-secondary" onClick={() => navigate('/auction/my/bids')}>
            Go to My Bids
          </button>
        </div>
      </div>
    </div>
  );
}
